<template>
    <header class="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/70">
        <!-- Contact strip -->
        <div class="hidden md:block bg-brand-800 text-brand-100 text-xs">
            <div class="container mx-auto px-4 lg:px-6 h-8 flex items-center justify-between">
                <span>Váš zabíjačkový servis. Už od roku 1991</span>
                <div class="flex items-center gap-5">
                    <span v-if="$brand.contact.openingHours">{{ $brand.contact.openingHours }}</span>
                    <a :href="$phoneLink()" class="hover:text-white transition-colors">{{ $brand.contact.phone }}</a>
                    <a :href="`mailto:${$brand.contact.email}`" class="hover:text-white transition-colors">{{ $brand.contact.email }}</a>
                </div>
            </div>
        </div>

        <!-- Main Navigation: links | logo | search, account, cart (mobile: logo | search, account) -->
        <nav class="container mx-auto px-4 lg:px-6">
            <div class="grid grid-cols-[auto_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16 lg:h-24">

                <!-- Left Section: main links + "Viac" dropdown (mobile: BottomNavBar) -->
                <div class="hidden lg:flex items-center">
                    <div class="hidden lg:flex items-center gap-1">
                        <router-link v-for="link in navLinks" :key="link.to" :to="link.to"
                            class="relative px-3 py-2 text-[15px] font-semibold transition-colors group"
                            :class="isActive(link.to) ? 'text-brand-900' : 'text-brand-700 hover:text-brand-900'">
                            {{ link.label }}
                            <span class="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-red-600 origin-left transition-transform duration-300"
                                :class="isActive(link.to) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'"></span>
                        </router-link>

                        <div class="relative more-dropdown">
                            <button @click="toggleMoreMenu" :aria-expanded="isMoreOpen"
                                class="flex items-center gap-1 px-3 py-2 text-[15px] font-semibold rounded-full transition-colors"
                                :class="isMoreOpen || moreLinks.some((l) => isActive(l.to)) ? 'text-brand-900' : 'text-brand-700 hover:text-brand-900'">
                                Viac
                                <ChevronDown class="h-4 w-4 transition-transform" :class="{ 'rotate-180': isMoreOpen }" />
                            </button>
                            <div v-if="isMoreOpen"
                                class="absolute left-0 top-full mt-2 w-52 py-2 bg-white rounded-2xl shadow-xl ring-1 ring-brand-100 z-50">
                                <router-link v-for="link in moreLinks" :key="link.to" :to="link.to"
                                    class="block px-4 py-2.5 text-sm font-medium transition-colors"
                                    :class="isActive(link.to) ? 'text-brand-900 bg-brand-50' : 'text-brand-700 hover:bg-brand-50 hover:text-brand-900'">
                                    {{ link.label }}
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Center Section: Logo -->
                <router-link to="/" class="justify-self-start lg:justify-self-center lg:px-4">
                    <!-- Mobile: the wide wordmark, desktop: the round logo -->
                    <img src="@brand/assets/delikat-navbar-mobile.webp" :alt="$brand.siteName" width="2386" height="1193"
                        class="lg:hidden h-12 w-auto">
                    <img src="@brand/assets/logo_underline.png" :alt="$brand.siteName"
                        class="hidden lg:block h-20">
                </router-link>

                <!-- Right Section: Search, Account, Cart -->
                <div class="flex items-center justify-end gap-1 sm:gap-2">
                    <!-- Search toggle (the mobile home page has its own search bar) -->
                    <button @click="toggleSearch" aria-label="Hľadať"
                        class="p-2 hover:bg-brand-50 rounded-full transition-colors search-panel"
                        :class="{ 'bg-brand-50': isSearchOpen, 'hidden lg:block': route.path === '/' }">
                        <X v-if="isSearchOpen" class="h-6 w-6 text-brand-800" />
                        <Search v-else class="h-6 w-6 text-brand-800" />
                    </button>

                    <!-- User Menu -->
                    <div v-if="isAuthenticated" class="relative user-dropdown">
                        <button @click="toggleUserMenu"
                            class="flex items-center gap-2 p-1.5 hover:bg-brand-50 rounded-full transition-colors group">
                            <UserAvatar :user="user" class="w-9 h-9 text-sm ring-2 ring-brand-100" />
                            <!-- Name on mobile and wide desktops (no room for it on small desktops) -->
                            <span class="lg:hidden xl:block max-w-28 truncate pr-1 xl:pr-0 text-sm font-medium text-brand-800">
                                {{ user?.first_name }}
                            </span>
                            <ChevronDown class="hidden xl:block h-4 w-4 text-brand-500" />
                        </button>
                    </div>

                    <!-- Login (for non-authenticated users) -->
                    <router-link v-else to="/auth/login" aria-label="Prihlásenie"
                        class="flex items-center gap-2 p-2 hover:bg-brand-50 rounded-full transition-colors">
                        <User class="h-6 w-6 text-brand-800" />
                        <span class="hidden xl:block text-sm font-medium text-brand-800 pr-1">Prihlásenie</span>
                    </router-link>

                    <!-- Cart (mobile: BottomNavBar) -->
                    <div class="relative cart-dropdown">
                        <button @click="toggleCartDropdown"
                            class="hidden lg:flex items-center gap-2 pl-3 pr-4 py-2 bg-brand-800 hover:bg-brand-900 text-white rounded-full transition-colors">
                            <span class="relative">
                                <ShoppingCart class="h-5 w-5" />
                                <span v-if="cartStore.totalQuantity > 0"
                                    class="absolute -top-2.5 -right-2.5 bg-red-600 text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-semibold ring-2 ring-brand-800">
                                    {{ cartStore.totalQuantity > 99 ? '😋' : cartStore.totalQuantity }}
                                </span>
                            </span>
                            <span class="text-sm font-semibold">{{ formatPrice(cartStore.total || 0) }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Search Panel -->
            <div v-if="isSearchOpen" class="pb-4 animate-in slide-in-from-top search-panel">
                <div class="relative max-w-2xl mx-auto" ref="searchDropdownRef">
                    <div class="relative">
                        <input v-model="searchQuery" ref="searchInput" type="text" placeholder="Hľadať produkty, kategórie..."
                            class="w-full pl-12 pr-12 py-3 truncate placeholder:truncate text-sm bg-brand-50 border border-brand-100 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent focus:bg-white transition-all duration-200 font-poppins"
                            @input="handleSearchInputEvent" @keydown="onSearchKeydown" @focus="handleSearchFocus">
                        <Search class="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-brand-400" />

                        <!-- Clear button -->
                        <button v-if="searchQuery" @click="clearSearch"
                            class="absolute right-12 top-1/2 transform -translate-y-1/2 p-1 hover:text-brand-700 transition-colors">
                            <X class="h-4 w-4 text-gray-400" />
                        </button>

                        <!-- Search button -->
                        <button @click="performSearch"
                            class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors">
                            <Search class="h-4 w-4" />
                        </button>
                    </div>

                    <SearchResultsDropdown v-if="isDropdownOpen && (hasResults || isLoading)" :results="searchResults"
                        :query="searchQuery" :selected-index="selectedIndex" :loading="isLoading"
                        :highlight="highlightMatch" @select="selectItem" @search-all="performSearch" />
                </div>
            </div>
        </nav>


        <!-- Cart Dropdown - Modern Mobile-First Design -->
        <div v-if="isCartDropdownOpen"
            class="fixed sm:absolute left-0 right-0 sm:left-auto sm:right-4 bottom-0 sm:bottom-auto sm:top-full sm:mt-2 w-full sm:w-[420px] max-h-[90vh] sm:max-h-[600px] bg-white/95 sm:bg-white backdrop-blur-xl sm:backdrop-blur-none rounded-t-3xl sm:rounded-2xl shadow-2xl border-t sm:border border-gray-200/50 z-50 animate-in slide-in-from-bottom sm:slide-in-from-top duration-300 cart-dropdown flex flex-col"
            @click.stop>
            
            <!-- Mobile Handle Bar -->
            <div class="sm:hidden flex justify-center pt-2 pb-1 flex-shrink-0">
                <div class="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            <!-- Header -->
            <div class="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="relative">
                            <div class="w-10 h-10 bg-brand-700 rounded-xl flex items-center justify-center shadow-lg shadow-brand-700/20">
                                <ShoppingCart class="h-5 w-5 text-white" />
                            </div>
                            <div v-if="cartStore.itemCount > 0" 
                                class="absolute -top-1 -right-1 w-5 h-5 bg-brand-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white">
                                {{ cartStore.itemCount > 9 ? '9+' : cartStore.itemCount }}
                            </div>
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-gray-900">Košík</h3>
                            <p class="text-xs text-gray-500">
                                {{ cartStore.itemCount }} {{ cartStore.itemCount === 1 ? 'položka' : cartStore.itemCount < 5 ? 'položky' : 'položiek' }}
                            </p>
                        </div>
                    </div>
                    <button @click="closeCartDropdown" 
                        class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-all active:scale-95">
                        <X class="h-5 w-5 text-gray-400" />
                    </button>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="cartStore.itemCount === 0" class="flex-1 flex flex-col items-center justify-center px-4 py-8 min-h-[300px]">
                <div class="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ShoppingCart class="h-10 w-10 text-gray-400" />
                </div>
                <h4 class="text-base font-semibold text-gray-900 mb-1">Košík je prázdny</h4>
                <p class="text-sm text-gray-500 mb-6">Pridajte produkty do košíka</p>
                <router-link to="/products"
                    class="inline-flex items-center gap-2 px-6 py-3 bg-brand-700 text-white rounded-xl hover:bg-brand-800 transition-all shadow-lg shadow-brand-700/20 font-medium active:scale-95"
                    @click="closeCartDropdown">
                    <ShoppingCart class="h-4 w-4" />
                    Začať nakupovať
                </router-link>
            </div>

            <!-- Cart Items -->
            <div v-else class="flex flex-col" style="max-height: calc(100vh - 280px);">
                <!-- Items List -->
                <div class="flex-1 overflow-y-auto overscroll-contain">
                    <div class="px-3 sm:px-4 py-2 space-y-2">
                        <div v-for="item in cartItems" :key="item.id"
                            class="group relative bg-gray-50/50 hover:bg-gray-50 rounded-xl p-3 transition-all">
                            <div class="flex gap-3">
                                <!-- Image -->
                                <router-link
                                    :to="cartItemLink(item)"
                                    @click="closeCartDropdown"
                                    class="relative flex-shrink-0">
                                    <div class="w-16 h-16 rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm group-hover:shadow-md transition-shadow">
                                        <img v-if="item.is_custom_mix && item.custom_mix?.package?.image"
                                            :src="item.custom_mix.package.image"
                                            :alt="item.custom_mix.name"
                                            class="w-full h-full object-cover">
                                        <img v-else-if="item.is_set_item && item.set_metadata?.image"
                                            :src="item.set_metadata.image"
                                            :alt="item.set_metadata.name"
                                            class="w-full h-full object-cover">
                                        <img v-else-if="!item.is_set_item && !item.is_custom_mix && item.product?.main_image"
                                            :src="item.product.main_image"
                                            :alt="item.product.name"
                                            class="w-full h-full object-cover">
                                        <div v-else class="w-full h-full flex items-center justify-center bg-amber-50">
                                            <span class="text-xl">📦</span>
                                        </div>
                                    </div>
                                    <!-- Quantity Badge -->
                                    <div class="absolute -top-1.5 -left-1.5 w-6 h-6 bg-gray-900 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                                        {{ item.quantity }}
                                    </div>
                                </router-link>

                                <!-- Info -->
                                <div class="flex-1 min-w-0">
                                    <router-link
                                        :to="cartItemLink(item)"
                                        @click="closeCartDropdown">
                                        <h4 class="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-brand-700 transition-colors leading-snug mb-1.5">
                                            {{ item.is_custom_mix ? `Vlastná zmes „${item.custom_mix?.name}“` : item.is_set_item ? item.set_metadata?.name : (item.product?.name || 'Product') }}
                                        </h4>
                                    </router-link>

                                    <!-- Own spice mix: package and spices -->
                                    <p v-if="item.is_custom_mix" class="text-xs text-gray-500 line-clamp-2 mb-1">
                                        {{ item.custom_mix?.package?.name }} · {{ (item.custom_mix?.spices || []).map((spice) => `${spice.name} ${formatMixGrams(spice.grams)}`).join(', ') }}
                                    </p>

                                    <!-- Set badge -->
                                    <span v-if="item.is_set_item" class="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full mb-1">
                                        Set
                                    </span>

                                    <!-- Pricing Row -->
                                    <div class="flex items-center justify-between gap-2">
                                        <div class="flex items-center gap-1.5 flex-wrap">
                                            <!-- Set pricing -->
                                            <template v-if="item.is_set_item">
                                                <span class="text-sm font-bold text-amber-700">
                                                    {{ formatPrice(item.unit_price) }}
                                                </span>
                                            </template>
                                            <!-- B2B Custom Pricing -->
                                            <template v-else-if="item.product?.has_custom_pricing">
                                                <span class="text-sm font-bold text-blue-600">
                                                    {{ item.product?.formatted_price }}
                                                </span>
                                                <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full">
                                                    <Star class="h-2.5 w-2.5" />
                                                    B2B
                                                </span>
                                            </template>
                                            <!-- Regular Pricing -->
                                            <template v-else>
                                                <span class="text-sm font-bold text-gray-900">
                                                    {{ formatPrice(item.final_price) }}
                                                </span>
                                                <span v-if="item.has_bulk_discount"
                                                    class="inline-flex items-center px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded-full">
                                                    -{{ item.discount_percentage }}%
                                                </span>
                                            </template>
                                        </div>

                                        <!-- Total Price -->
                                        <div class="text-right">
                                            <div class="text-base font-black text-gray-900">
                                                {{ item.is_set_item
                                                    ? formatPrice(item.unit_price * item.quantity)
                                                    : item.product?.has_custom_pricing
                                                        ? formatPrice(item.quantity * parseFloat(item.product?.custom_pricing?.custom_price || item.final_price))
                                                        : formatPrice(item.total_price) }}
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Savings Badge (regular products only) -->
                                    <div v-if="!item.is_set_item && ((item.total_savings > 0 && !item.product?.has_custom_pricing) || 
                                              (item.product?.has_custom_pricing && parseFloat(item.unit_price) > parseFloat(item.product?.custom_pricing?.custom_price || item.final_price)))"
                                        class="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-semibold rounded-md">
                                        <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                        </svg>
                                        Ušetrené {{ item.product?.has_custom_pricing 
                                            ? formatPrice(item.quantity * (parseFloat(item.unit_price) - parseFloat(item.product?.custom_pricing?.custom_price || item.final_price)))
                                            : formatPrice(item.total_savings) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer - Sticky -->
                <div class="border-t border-gray-200 bg-white">
                    <!-- Total Savings Banner -->
                    <div v-if="cartStore.totalSavings > 0" 
                        class="mx-3 sm:mx-4 mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <div class="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                    <svg class="h-3.5 w-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                    </svg>
                                </div>
                                <span class="text-xs font-semibold text-green-800">Celková úspora</span>
                            </div>
                            <span class="text-sm font-black text-green-700">{{ formatPrice(cartStore.totalSavings) }}</span>
                        </div>
                    </div>
                    
                    <!-- Total -->
                    <div class="px-4 sm:px-5 py-4">
                        <div class="flex items-center justify-between mb-4">
                            <span class="text-sm font-medium text-gray-600">Medzisúčet</span>
                            <div class="text-right">
                                <div class="text-2xl font-black text-gray-900">
                                    {{ formatPrice(cartStore.total) }}
                                </div>
                                <p class="text-xs text-gray-500">+ doprava pri pokladni</p>
                            </div>
                        </div>
                        
                        <!-- Action Buttons -->
                        <div class="flex gap-2">
                            <router-link to="/checkout"
                                class="flex-[2] px-4 py-3 bg-brand-700 hover:bg-brand-800 text-white text-center rounded-xl transition-all font-bold text-sm shadow-lg shadow-brand-700/20 active:scale-95"
                                @click="closeCartDropdown">
                                Do košíka →
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- User Menu Dropdown -->
        <div v-if="isUserMenuOpen && isAuthenticated"
            class="absolute right-4 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 animate-in slide-in-from-top duration-300 user-dropdown">
            <!-- User Header -->
            <div class="bg-white border-b border-gray-200 p-4">
                <div class="flex items-center space-x-3">
                    <UserAvatar :user="user" class="w-12 h-12 text-lg shrink-0" />
                    <div>
                        <p class="font-semibold text-gray-900 font-poppins">{{ user?.first_name || 'Používateľ' }}</p>
                        <p class="text-sm text-gray-600">{{ user?.email }}</p>
                    </div>
                </div>
            </div>

            <!-- Menu Items -->
            <div class="p-2">
                <div class="space-y-1">
                    <router-link to="/user"
                        class="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
                        @click="closeUserMenu">
                        <div class="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                            <User class="h-4 w-4 text-gray-600 group-hover:text-brand-700" />
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">Môj profil</p>
                            <p class="text-xs text-gray-500">Nastavenia účtu</p>
                        </div>
                    </router-link>

                    <router-link to="/user/orders"
                        class="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
                        @click="closeUserMenu">
                        <div class="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package class="h-4 w-4 text-gray-600 group-hover:text-brand-700" />
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">Moje objednávky</p>
                            <p class="text-xs text-gray-500">História nákupov</p>
                        </div>
                    </router-link>

                    <router-link v-if="user?.role === 'admin'" to="/admin"
                        class="flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
                        @click="closeUserMenu">
                        <div class="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Settings class="h-4 w-4 text-gray-600 group-hover:text-brand-700" />
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-900">Admin panel</p>
                            <p class="text-xs text-gray-500">Správa systému</p>
                        </div>
                    </router-link>
                </div>

                <div class="mt-2 pt-2 border-t border-gray-200">
                    <button @click="handleLogout"
                        class="flex items-center space-x-3 w-full px-3 py-2.5 text-brand-700 hover:bg-brand-50 rounded-lg transition-colors group">
                        <div class="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
                            <LogOut class="h-4 w-4 text-brand-700" />
                        </div>
                        <span class="text-sm font-medium">Odhlásiť sa</span>
                    </button>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useSearchDropdown } from '@/composables/useSearchDropdown'
import { sortCartItems, formatMixGrams } from '@/composables/useCheckout'
import { useB2BPricing } from '@/composables/useB2BPricing'
import { pagePath, hasFeature } from '@/brand'
import SearchResultsDropdown from '@/components/nav/SearchResultsDropdown.vue'
import UserAvatar from '@/components/user/UserAvatar.vue'
import {
    ShoppingCart,
    ChevronDown,
    X,
    Search,
    Star,
    User,
    Package,
    Settings,
    LogOut
} from 'lucide-vue-next'

const authStore = useAuthStore()
const cartStore = useCartStore()
const cartItems = computed(() => sortCartItems(cartStore.cart?.items))
const router = useRouter()
const route = useRoute()

// B2B pricing composable
const { formatPrice } = useB2BPricing()

const navLinks = [
    { label: 'Produkty', to: '/products' },
    { label: 'Sprievodca', to: '/sprievodca' }
]

// Behind "Viac", so the left side stays as narrow as the right one
const moreLinks = [
    { label: 'Kategórie', to: '/categories' },
    { label: 'O nás', to: pagePath('about') },
    { label: 'Kontakt', to: pagePath('contact') }
]

const isMoreOpen = ref(false)
const toggleMoreMenu = () => {
    isMoreOpen.value = !isMoreOpen.value
}

const isActive = (to) => route.path === to || route.path.startsWith(`${to}/`)

// Component state
const isCartDropdownOpen = ref(false)
const isUserMenuOpen = ref(false)
const isSearchOpen = ref(false)
const searchInput = ref(null)
const searchDropdownRef = ref(null)

// Search dropdown functionality
const {
    searchQuery,
    isDropdownOpen,
    isLoading,
    searchResults,
    selectedIndex,
    hasResults,
    handleInput: handleSearchInput,
    handleKeydown: handleSearchKeydown,
    selectItem,
    navigateToSearch: performSearch,
    clearSearch,
    highlightMatch,
    handleClickOutside: handleSearchClickOutside
} = useSearchDropdown()

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)

// Cart dropdown methods
const toggleCartDropdown = () => {
    isCartDropdownOpen.value = !isCartDropdownOpen.value
    isUserMenuOpen.value = false
    isSearchOpen.value = false
}

const closeCartDropdown = () => {
    isCartDropdownOpen.value = false
}

// Where a cart line leads: its product, its set, or the builder for an own spice mix
const cartItemLink = (item) => {
    if (item.is_custom_mix) return { name: 'mix-builder' }
    return item.is_set_item ? `/sets/${item.set_metadata?.slug}` : `/products/${item.product?.slug}`
}

// User menu methods
const toggleUserMenu = () => {
    isUserMenuOpen.value = !isUserMenuOpen.value
    isCartDropdownOpen.value = false
    isSearchOpen.value = false
}

const closeUserMenu = () => {
    isUserMenuOpen.value = false
}

// Search panel methods
const toggleSearch = () => {
    isSearchOpen.value = !isSearchOpen.value
    isCartDropdownOpen.value = false
    isUserMenuOpen.value = false

    if (isSearchOpen.value) {
        nextTick(() => searchInput.value?.focus())
    }
}

const closeSearch = () => {
    isSearchOpen.value = false
}

// Enter/Escape with the dropdown open are handled by useSearchDropdown
const onSearchKeydown = (event) => {
    if (!isDropdownOpen.value || !hasResults.value) {
        if (event.key === 'Enter') performSearch()
        if (event.key === 'Escape') closeSearch()
    }
    handleSearchKeydown(event)
}

// Search focus handler
const handleSearchFocus = () => {
    if (searchQuery.value.length >= 2) {
        handleSearchInput(searchQuery.value)
    }
}

// Fix search input handler to extract value from event
const handleSearchInputEvent = (event) => {
    handleSearchInput(event.target.value)
}

// Logout handler
const handleLogout = async () => {
    await authStore.logout()
    closeUserMenu()
    router.push('/')
}

// Close the search panel after navigating to a result
watch(() => route.fullPath, () => {
    isSearchOpen.value = false
    isMoreOpen.value = false
})

// Click outside handlers. composedPath() is captured at dispatch time, so it
// still holds the clicked element's ancestors when the click itself re-rendered
// (and detached) it, e.g. the search icon swapping to X.
const handleGlobalClickOutside = (event) => {
    const path = event.composedPath()
    const inside = (cls) => path.some((el) => el.classList?.contains(cls))

    // Close search dropdown when clicking outside
    if (searchDropdownRef.value && !path.includes(searchDropdownRef.value)) {
        handleSearchClickOutside(event, searchDropdownRef)
    }

    // Close other dropdowns
    if (!inside('search-panel')) {
        isSearchOpen.value = false
    }
    if (!inside('cart-dropdown')) {
        isCartDropdownOpen.value = false
    }
    if (!inside('user-dropdown')) {
        isUserMenuOpen.value = false
    }
    if (!inside('more-dropdown')) {
        isMoreOpen.value = false
    }
}

onMounted(() => {
    // Add click outside listener
    document.addEventListener('click', handleGlobalClickOutside)

    // Logins from before profile pictures existed have no avatar in the stored user
    if (hasFeature('avatars') && authStore.user && !('avatar' in authStore.user)) {
        authStore.refreshUser().catch(() => {})
    }
})

onUnmounted(() => {
    // Clean up event listeners
    document.removeEventListener('click', handleGlobalClickOutside)
})
</script>


<style scoped>
/* Custom animations for mobile elements */
@keyframes slide-in-from-top {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-in {
    animation-duration: 300ms;
    animation-timing-function: ease-out;
    animation-fill-mode: both;
}

.slide-in-from-top {
    animation-name: slide-in-from-top;
}

/* Backdrop blur support */
@supports (backdrop-filter: blur(12px)) {
    .backdrop-blur-md {
        backdrop-filter: blur(12px);
    }
}

/* Smooth transitions */
.transition-colors {
    transition-property: color, background-color, border-color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
}

/* Custom scrollbar for cart items */
.max-h-96::-webkit-scrollbar {
    width: 4px;
}

.max-h-96::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
}

.max-h-96::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
}

.max-h-96::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Line clamp utility */
.line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
}
</style>